import React, { useEffect, useState } from "react";
import { useCookieConsent } from "../context/CookieConsentContext";
import { Link } from "react-router-dom";
import { createPortal } from "react-dom";
import ButtonRegular from "./ButtonRegular";

const CookieBanner = () => {
  const { isBannerOpen, prefs, acceptAll, rejectAll, savePrefs, closeBanner } = useCookieConsent();
  const [localPrefs, setLocalPrefs] = useState(prefs);
  const [isCustomizeOpen, setCustomizeOpen] = useState(false);

  useEffect(() => {
    if (isCustomizeOpen) {
      document.body.classList.add('cookie-modal-open');
    } else {
      document.body.classList.remove('cookie-modal-open');
    }
    return () => document.body.classList.remove('cookie-modal-open');
  }, [isCustomizeOpen]);

  if (!isBannerOpen) return null;

  const onToggle = (key) => setLocalPrefs((p) => ({ ...p, [key]: !p[key] }));

  /* Portal to body: .app uses transform for lang-fade, which makes fixed descendants
   * position against .app — cookie bar would sit at the document bottom (off-screen). */
  const bannerPortal = createPortal(
    <div
      className={`cookie-banner ${isCustomizeOpen ? "modal-open" : ""}`}
      role="dialog"
      aria-live="polite"
      aria-labelledby="cookie-banner-title"
    >
      <div className="cookie-content">
        <div className="cookie-text">
          <h3 id="cookie-banner-title">
            <span className="desktop">szanujemy twoją prywatność</span>
            <span className="mobile">szanujemy prywatność</span>
          </h3>
          <p className="desktop">
            Używamy cookies w celach analitycznych, aby ulepszać stronę. Zaakceptuj lub dostosuj
            ustawienia. Niezbędne ciastka są zawsze włączone.
          </p>
          <p className="mobile">
            Używamy{" "}
            <Link to="/cookie-policy" className="cookies-link-mobile" onClick={closeBanner}>
              cookies
            </Link>
            , aby ulepszać stronę.
          </p>
          <span className="necessary-text mobile">Niezbędne powinny być włączone.</span>
        </div>
        <div className="cookie-actions">
          <ButtonRegular className="btn-gap" onClick={acceptAll}>
            Akceptuj
          </ButtonRegular>
          <button
            className="link"
            onClick={() => setCustomizeOpen(true)}
            aria-expanded={isCustomizeOpen}
          >
            Dostosuj
          </button>
          <div className="cookie-divider" />
          <button className="link" onClick={rejectAll}>
            Odrzuć
          </button>
          <Link to="/cookie-policy" className="link desktop" onClick={closeBanner}>
            <svg
              className="desktop"
              width="24"
              height="25"
              viewBox="0 0 24 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g id="verified_user_24dp_5F6368_FILL0_wght400_GRAD0_opsz24 1">
                <path
                  id="Vector"
                  d="M10.95 16.1516L16.6 10.5016L15.175 9.07656L10.95 13.3016L8.85 11.2016L7.425 12.6266L10.95 16.1516ZM12 22.6016C9.68333 22.0182 7.77083 20.6891 6.2625 18.6141C4.75417 16.5391 4 14.2349 4 11.7016V5.60156L12 2.60156L20 5.60156V11.7016C20 14.2349 19.2458 16.5391 17.7375 18.6141C16.2292 20.6891 14.3167 22.0182 12 22.6016ZM12 20.5016C13.7333 19.9516 15.1667 18.8516 16.3 17.2016C17.4333 15.5516 18 13.7182 18 11.7016V6.97656L12 4.72656L6 6.97656V11.7016C6 13.7182 6.56667 15.5516 7.7 17.2016C8.83333 18.8516 10.2667 19.9516 12 20.5016Z"
                  fill="#FF9459"
                />
              </g>
            </svg>
            Polityka cookies
          </Link>
        </div>
      </div>
    </div>,
    document.body
  );

  const modalPortal = isCustomizeOpen ? createPortal(
    (
      <div className="cookie-modal" role="dialog" aria-modal="true" aria-labelledby="cookie-modal-title">
        <div className="cookie-modal-content">
          <div className="cookie-modal-header">
            <h3 id="cookie-modal-title">Ustawienia cookies</h3>
            <button className="close" aria-label="Zamknij" onClick={() => setCustomizeOpen(false)}>×</button>
          </div>
          <div className="cookie-modal-body">
            <div className="row between">
              <div className="pref-info">
                <span className="title">Niezbędne (zawsze aktywne)</span>
                <span className="desc">Zapewniają podstawowe działanie serwisu (nawigacja, bezpieczeństwo, sesja). Nie można ich wyłączyć.</span>
              </div>
              <label className="cookie-toggle" aria-disabled>
                <input type="checkbox" checked readOnly />
                <span className="cookie-slider"></span>
              </label>
            </div>
            <div className="row between">
              <div className="pref-info">
                <span className="title">Funkcjonalne</span>
                <span className="desc">Zapamiętują Twoje preferencje i usprawniają korzystanie (np. język, ustawienia interfejsu).</span>
              </div>
              <label className="cookie-toggle">
                <input type="checkbox" checked={localPrefs.functional} onChange={() => onToggle('functional')} />
                <span className="cookie-slider"></span>
              </label>
            </div>
            <div className="row between">
              <div className="pref-info">
                <span className="title">Analityczne</span>
                <span className="desc">Pomagają mierzyć ruch i wydajność serwisu w oparciu o zanonimizowane statystyki.</span>
              </div>
              <label className="cookie-toggle">
                <input type="checkbox" checked={localPrefs.analytics} onChange={() => onToggle('analytics')} />
                <span className="cookie-slider"></span>
              </label>
            </div>
            <div className="row between">
              <div className="pref-info">
                <span className="title">Marketingowe</span>
                <span className="desc">Umożliwiają personalizację treści i reklam oraz pomiar ich skuteczności.</span>
              </div>
              <label className="cookie-toggle">
                <input type="checkbox" checked={localPrefs.marketing} onChange={() => onToggle('marketing')} />
                <span className="cookie-slider"></span>
              </label>
            </div>
          </div>
          <div className="cookie-modal-footer">
            <button className="btn-link" onClick={rejectAll}>Tylko niezbędne</button>
            <button className="btn-link" onClick={() => { savePrefs(localPrefs); setCustomizeOpen(false); }}>Zapisz wybór</button>
          </div>
        </div>
      </div>
    ),
    document.body
  ) : null;

  return (
    <>
      {bannerPortal}
      {modalPortal}
    </>
  );
};

export default CookieBanner;


